import type { SalesDailyChecklistItem } from "@/lib/salesDailyChecklistClient";
import { parseOppTodoChecklistItemId, oppTodoUrgencyRowClass } from "@/lib/opportunity/oppTodoChecklistUrgency";
import { openOpportunityWorkspace } from "@/lib/opportunity/openOpportunityWorkspace";

export function SalesDailyChecklistItemRow(props: {
  it: SalesDailyChecklistItem;
  loading: boolean;
  togglingId: string | null;
  onToggle: (item: SalesDailyChecklistItem, nextDone: boolean) => void;
  setPendingConfirm: (item: SalesDailyChecklistItem | null) => void;
  onOpenAdsAssistant?: () => void;
  onOpenAdsAssistantSuggestion?: () => void;
}) {
  const { it, loading, togglingId, onToggle, setPendingConfirm, onOpenAdsAssistant, onOpenAdsAssistantSuggestion } =
    props;

  const isOppTodo = parseOppTodoChecklistItemId(it.id) != null;

  return (
    <li
      className={
        "flex items-start gap-3 rounded-xl border px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 " +
        (isOppTodo && it.opp_todo_urgency
          ? oppTodoUrgencyRowClass(it.opp_todo_urgency)
          : "border-gray-200 bg-white dark:border-gray-600 dark:bg-zinc-900")
      }
    >
      {it.kind === "manual" ? (
        <input
          type="checkbox"
          checked={it.done}
          disabled={togglingId === it.id || loading}
          onChange={(e) => {
            const on = e.target.checked;
            if (!on) {
              void onToggle(it, false);
              return;
            }
            if (it.requires_confirm) setPendingConfirm(it);
            else void onToggle(it, true);
          }}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-500"
          aria-label={it.label}
        />
      ) : (
        <span className="mt-0.5 text-base text-gray-400 dark:text-gray-500" aria-hidden title="Automatic">
          {it.done ? "☑" : "☐"}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className={it.done ? "font-medium text-emerald-800 dark:text-emerald-300" : "text-gray-900 dark:text-gray-100"}>
          {it.label}
        </div>
        {it.description ? <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{it.description}</p> : null}
        {it.ad_progress ? (
          <div className="mt-2 space-y-1.5">
            <p className="text-[11px] font-medium text-gray-700 dark:text-gray-300 tabular-nums">
              {it.ad_progress.done} / {it.ad_progress.target} toward today&apos;s ad goal
            </p>
            <div className="flex flex-wrap gap-1" aria-hidden>
              {Array.from({ length: it.ad_progress.target }, (_, i) => (
                <span
                  key={i}
                  className={
                    i < it.ad_progress!.done
                      ? "h-2 w-6 rounded-sm bg-emerald-500 dark:bg-emerald-400"
                      : "h-2 w-6 rounded-sm bg-gray-200 dark:bg-gray-600"
                  }
                />
              ))}
            </div>
            {it.ad_progress.by_platform && it.ad_progress.by_platform.length > 0 ? (
              <ul className="mt-1.5 space-y-0.5 text-[10px] text-gray-600 dark:text-gray-400">
                {it.ad_progress.by_platform.map((p) => (
                  <li key={p.key} className="tabular-nums">
                    {p.label}: {p.done}/{p.target}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        {it.id === "ad_logged" && it.ad_progress && it.ad_progress.done < it.ad_progress.target ? (
          <div className="mt-2 flex flex-wrap gap-3">
            {onOpenAdsAssistant ? (
              <button
                type="button"
                onClick={onOpenAdsAssistant}
                className="text-xs text-blue-600 hover:underline dark:text-blue-400"
              >
                Open Ads Ingestion
              </button>
            ) : null}
            {onOpenAdsAssistantSuggestion ? (
              <button
                type="button"
                onClick={onOpenAdsAssistantSuggestion}
                className="text-xs text-blue-600 hover:underline dark:text-blue-400"
              >
                Open Ads Suggestion
              </button>
            ) : null}
          </div>
        ) : null}
        {isOppTodo && it.opp_todo_opportunity_id ? (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => openOpportunityWorkspace(it.opp_todo_opportunity_id!, "todos")}
              className="text-xs font-medium text-blue-700 hover:underline dark:text-blue-300"
            >
              Open deal todos
            </button>
          </div>
        ) : null}
      </div>
    </li>
  );
}
